import {
  CASAMAPA_QUIZ_HOUSEHOLDS,
  CASAMAPA_QUIZ_LIFESTYLE_SEARCH_KEYS,
  CASAMAPA_QUIZ_LIFESTYLES,
  CASAMAPA_QUIZ_MAX_MUNICIPALITIES,
  CASAMAPA_QUIZ_MUNICIPALITIES,
  CASAMAPA_QUIZ_PROPERTY_TYPES,
  CASAMAPA_QUIZ_STEP_IDS,
  CASAMAPA_QUIZ_TIMELINES,
  CASAMAPA_QUIZ_VERSION,
} from "./casaMapaQuizConstants";
import {
  createDefaultCasaMapaSearchState,
  serializeCasaMapaSearchParams,
} from "./casaMapaSearchParams";
import { runtimeSearchPath } from "../../clientViewRuntime/utils/routes";

const ALLOWED_MUNICIPALITIES = new Set(
  CASAMAPA_QUIZ_MUNICIPALITIES.map((row) => row.value)
);
const ALLOWED_TYPES = new Set(CASAMAPA_QUIZ_PROPERTY_TYPES);

function pickPositiveInt(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) {
    return null;
  }
  return Math.floor(n);
}

function householdToBedrooms(household) {
  switch (household) {
    case "just_me":
      return 1;
    case "couple":
      return 2;
    case "small_household":
      return 3;
    case "larger_household":
      return 4;
    default:
      return null;
  }
}

/**
 * Deterministic visible steps for the current answers.
 */
export function getVisibleCasaMapaQuizSteps(answers = {}) {
  const listingType = answers.listingType === "rent" ? "rent" : answers.listingType === "sale" ? "sale" : null;
  const types = Array.isArray(answers.propertyTypes) ? answers.propertyTypes : [];
  const isLotOnly = types.length === 1 && types[0] === "lot";

  return CASAMAPA_QUIZ_STEP_IDS.filter((stepId) => {
    if (stepId === "listingType") return true;
    if (!listingType && stepId !== "review") {
      return stepId === "listingType";
    }
    if (isLotOnly && (stepId === "bedrooms" || stepId === "bathsParking" || stepId === "area")) {
      return false;
    }
    return true;
  });
}

/**
 * Clear answers that become invalid when prior answers change.
 */
export function sanitizeCasaMapaQuizAnswers(answers = {}) {
  const next = { ...answers };
  if (next.listingType !== "sale" && next.listingType !== "rent") {
    next.listingType = null;
  }

  const municipalities = Array.isArray(next.municipalities)
    ? [...new Set(next.municipalities.filter((item) => ALLOWED_MUNICIPALITIES.has(item)))].slice(
        0,
        CASAMAPA_QUIZ_MAX_MUNICIPALITIES
      )
    : [];
  next.municipalities = municipalities;
  next.openToAreas = Boolean(next.openToAreas);

  const propertyTypes = Array.isArray(next.propertyTypes)
    ? [...new Set(next.propertyTypes.filter((item) => ALLOWED_TYPES.has(item)))]
    : [];
  next.propertyTypes = propertyTypes;
  next.propertyTypeAny = Boolean(next.propertyTypeAny) && propertyTypes.length === 0;

  if (propertyTypes.length === 1 && propertyTypes[0] === "lot") {
    next.bedroomsMin = undefined;
    next.bathroomsMin = undefined;
    next.parkingMin = undefined;
    next.constructedAreaMinM2 = undefined;
    next.household = null;
  }

  if (next.household && !CASAMAPA_QUIZ_HOUSEHOLDS.includes(next.household)) {
    next.household = null;
  }
  if (next.timeline && !CASAMAPA_QUIZ_TIMELINES.includes(next.timeline)) {
    next.timeline = null;
  }

  next.lifestyles = Array.isArray(next.lifestyles)
    ? [...new Set(next.lifestyles.filter((item) => CASAMAPA_QUIZ_LIFESTYLES.includes(item)))]
    : [];

  if (next.budgetUnknown) {
    next.budgetMaxCop = null;
  } else {
    const budget = pickPositiveInt(next.budgetMaxCop);
    next.budgetMaxCop = budget;
  }

  if (next.bedroomsMin !== undefined) {
    next.bedroomsMin = next.bedroomsMin === null ? null : pickPositiveInt(next.bedroomsMin);
  }
  if (next.bathroomsMin !== undefined) {
    next.bathroomsMin = next.bathroomsMin === null ? null : pickPositiveInt(next.bathroomsMin);
  }
  if (next.parkingMin !== undefined) {
    next.parkingMin = next.parkingMin === null ? null : pickPositiveInt(next.parkingMin);
  }
  if (next.constructedAreaMinM2 !== undefined) {
    next.constructedAreaMinM2 =
      next.constructedAreaMinM2 === null ? null : pickPositiveInt(next.constructedAreaMinM2);
  }

  return next;
}

export function validateCasaMapaQuizStep(stepId, answers) {
  const clean = sanitizeCasaMapaQuizAnswers(answers);
  switch (stepId) {
    case "listingType":
      return clean.listingType === "sale" || clean.listingType === "rent"
        ? { ok: true }
        : { ok: false, messageKey: "listingTypeRequired" };
    case "budget":
      if (clean.budgetUnknown || clean.budgetMaxCop != null) {
        return { ok: true };
      }
      return { ok: false, messageKey: "budgetRequired" };
    case "geography":
      if (clean.openToAreas || clean.municipalities.length) {
        return { ok: true };
      }
      return { ok: false, messageKey: "geographyRequired" };
    case "propertyType":
      if (clean.propertyTypeAny || clean.propertyTypes.length) {
        return { ok: true };
      }
      return { ok: false, messageKey: "propertyTypeRequired" };
    case "bedrooms":
    case "bathsParking":
    case "area":
    case "lifestyle":
    case "timeline":
    case "review":
      return { ok: true };
    default:
      return { ok: true };
  }
}

/**
 * Map quiz answers → Milestone 3 CasaMapa search state.
 * Precedence: explicit answer → derived (household/lifestyle) → omit.
 */
export function mapCasaMapaQuizAnswersToSearchState(answers, _facets = null, quizVersion = CASAMAPA_QUIZ_VERSION) {
  const clean = sanitizeCasaMapaQuizAnswers(answers);
  const state = createDefaultCasaMapaSearchState();
  state.listingType = clean.listingType;

  if (!clean.budgetUnknown && clean.budgetMaxCop != null) {
    state.priceMaxCop = clean.budgetMaxCop;
  }

  if (!clean.openToAreas && clean.municipalities.length === 1) {
    state.municipalities = [clean.municipalities[0]];
    state.municipality = clean.municipalities[0];
  } else if (!clean.openToAreas && clean.municipalities.length > 1) {
    state.municipalities = clean.municipalities.slice();
    state.municipality = clean.municipalities[0];
  }

  if (!clean.propertyTypeAny && clean.propertyTypes.length) {
    state.propertyTypes = clean.propertyTypes.slice();
  }

  if (clean.bedroomsMin !== undefined && clean.bedroomsMin !== null) {
    // Studio (0) maps to property type studio preference when not already set;
    // bedroom filter uses 1+ only — studio is property type.
    if (clean.bedroomsMin === 0) {
      if (!state.propertyTypes.includes("studio") && !clean.propertyTypeAny) {
        state.propertyTypes = [...new Set([...state.propertyTypes, "studio"])];
      }
    } else if (clean.bedroomsMin > 0) {
      state.bedroomsMin = clean.bedroomsMin;
    }
  } else if (clean.household) {
    const derived = householdToBedrooms(clean.household);
    if (derived != null) {
      state.bedroomsMin = derived;
    }
  }

  if (clean.bathroomsMin != null && clean.bathroomsMin > 0) {
    state.bathroomsMin = clean.bathroomsMin;
  }

  if (clean.parkingMin != null && clean.parkingMin > 0) {
    state.parkingMin = clean.parkingMin;
  } else if (clean.lifestyles.includes("parking") && clean.parkingMin === undefined) {
    state.parkingMin = 1;
  }

  if (clean.constructedAreaMinM2 != null && clean.constructedAreaMinM2 > 0) {
    state.constructedAreaMinM2 = clean.constructedAreaMinM2;
  } else if (clean.lifestyles.includes("more_space") && clean.constructedAreaMinM2 === undefined) {
    state.constructedAreaMinM2 = 80;
  }

  // openToAreas / open_municipalities clears geography filters.
  if (clean.lifestyles.includes("open_municipalities") || clean.openToAreas) {
    state.municipalities = [];
    state.municipality = null;
  }

  return {
    searchState: state,
    quizVersion,
    unsupportedLifestyles: clean.lifestyles.filter(
      (item) => !CASAMAPA_QUIZ_LIFESTYLE_SEARCH_KEYS.has(item)
    ),
  };
}

export function mapCasaMapaQuizAnswersToHousingPrefs(answers, { locale = null, currency = "COP" } = {}) {
  const clean = sanitizeCasaMapaQuizAnswers(answers);
  const prefs = {
    intent: clean.listingType || null,
    budgetMinCop: null,
    budgetMaxCop: clean.budgetUnknown ? null : clean.budgetMaxCop,
    propertyTypes: clean.propertyTypeAny ? [] : clean.propertyTypes.slice(0, 8),
    bedroomsMin:
      clean.bedroomsMin != null && clean.bedroomsMin > 0
        ? clean.bedroomsMin
        : householdToBedrooms(clean.household),
    bathroomsMin: clean.bathroomsMin != null && clean.bathroomsMin > 0 ? clean.bathroomsMin : null,
    parkingMin: clean.parkingMin != null && clean.parkingMin > 0 ? clean.parkingMin : null,
    municipalities: clean.openToAreas ? [] : clean.municipalities.slice(0, 8),
    neighborhoods: [],
    preferredLocale: locale,
    preferredCurrency: currency,
  };
  return prefs;
}

export function buildCasaMapaQuizSearchHref({
  slug,
  locale = null,
  manifest = null,
  searchState,
  source = "quiz",
}) {
  const params = serializeCasaMapaSearchParams(searchState || createDefaultCasaMapaSearchState());
  if (source) {
    params.set("source", source);
  }
  const query = params.toString();
  const base = runtimeSearchPath(slug, locale, manifest);
  return query ? `${base}?${query}` : base;
}

export function suggestCasaMapaQuizBroadening(searchState = {}) {
  const suggestions = [];
  if (searchState.neighborhood) {
    suggestions.push({ id: "remove_neighborhood", patch: { neighborhood: null } });
  }
  if (searchState.municipality || (Array.isArray(searchState.municipalities) && searchState.municipalities.length)) {
    suggestions.push({
      id: "expand_geography",
      patch: { municipality: null, municipalities: [] },
    });
  }
  if (searchState.priceMaxCop != null) {
    suggestions.push({
      id: "raise_budget",
      patch: { priceMaxCop: Math.round(searchState.priceMaxCop * 1.25) },
    });
  }
  if (searchState.bedroomsMin != null && searchState.bedroomsMin > 1) {
    suggestions.push({
      id: "reduce_bedrooms",
      patch: { bedroomsMin: searchState.bedroomsMin - 1 },
    });
  }
  if (Array.isArray(searchState.propertyTypes) && searchState.propertyTypes.length) {
    suggestions.push({ id: "clear_property_types", patch: { propertyTypes: [] } });
  }
  if (searchState.constructedAreaMinM2 != null) {
    suggestions.push({ id: "clear_area", patch: { constructedAreaMinM2: null } });
  }
  return suggestions;
}
